// Our base component, which all others extend.
import { OutlineElement } from '../../base/outline-element/outline-element';
import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './tufts-grid.css.lit';
import componentStylesEqual from './tufts-grid-equal.css.lit';
import { styleMap } from 'lit/directives/style-map.js';
import { ResizeController } from '../../controllers/resize-controller';
import { classMap } from 'lit/directives/class-map.js';

/**
 * The tufts grid component
 * @element tufts-grid
 *
 * @slot - The default, and only slot for this element.
 *
 * @attr row-gap
 * @attr col-gap
 * @attr columns
 * @attr item-min-width
 * @attr align-self
 * @attr justify-self
 * @attr equal-height
 * @attr span-first
 */
@customElement('tufts-grid')
export class TuftsGrid extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, componentStylesEqual];

  /**
   * Vertical gap between items.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'row-gap',
  })
  rowGap = '1rem';

  /**
   * Horizontal gap between items.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'col-gap',
  })
  colGap = '1rem';

  /**
   * Maximum amount of columns.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'columns',
  })
  columns = '3';

  /**
   * Maximum amount of columns.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'span-first',
  })
  spanFirst: boolean;

  /**
   * Minimum width of each item
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'item-min-width',
  })
  itemMinWidth = '200px';

  /**
   * Minimum width of each item
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'align-self',
  })
  alignSelf = '';

  /**
   * Minimum width of each item
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'justify-self',
  })
  justifySelf = '';

  /**
   * Whether or not all rows will have equal height.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'equal-height',
  })
  equalHeight = false;

  /**
   * Whether or not all rows will have equal height force.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'equal-height-force',
  })
  equalHeightForce = false;
  resizeController: ResizeController;

  // The resizeController is initialized here because attributes are not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    if (this.spanFirst) {
      // Calculate the minimum width of two columns in pixels
      const minWidthOf2Columns =
        this.convertRemToPx(this.itemMinWidth) * 2 +
        this.convertRemToPx(this.colGap);

      // Initialize the ResizeController with the minimum width of two columns as a breakpoint
      this.resizeController = new ResizeController(this, {
        breakpoints: [minWidthOf2Columns - 1],
      });
    }
  }

  convertRemToPx(item: string) {
    if (item.endsWith('rem')) {
      return parseFloat(item) * 16;
    } else {
      return parseInt(item, 10);
    }
  }

  /**
   * Sets/removes the `span-two-columns` class on the element (:host) if the component's width is less than 2 columns.
   */
  spanFirstColumn(minWidthOf2Columns: boolean) {
    if (minWidthOf2Columns) {
      // eslint-disable-next-line
      this.classList.add('span-two-columns');
    } else {
      // eslint-disable-next-line
      this.classList.remove('span-two-columns');
    }
  }

  render(): TemplateResult {
    const minWidthOf2Columns =
      this.resizeController?.currentBreakpointRange > 0;

    if (this.spanFirst) {
      this.spanFirstColumn(minWidthOf2Columns);
    }

    const gridStyles = {
      '--grid-layout-column-gap': this.colGap,
      '--grid-layout-row-gap': this.rowGap,
      '--grid-column--max-count': this.columns,
      '--grid-item--min-width': `min(${this.itemMinWidth}, 100%)`,
      '--align-self': this.alignSelf || 'auto',
      '--justify-self': this.justifySelf || 'auto',
    };
    const classes = {
      'grid-wrapper': true,
      'grid-wrapper-grid': this.equalHeightForce,
      'align-self': this.alignSelf,
      'justify-self': this.justifySelf,
      'equal-height': this.equalHeight,
    };

    ('grid-wrapper-grid');
    return html` <div
      class="${classMap(classes)}"
      style=${styleMap(gridStyles)}
    >
      <slot></slot>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-grid': TuftsGrid;
  }
}
